<script setup lang="ts">
import {ref, watch} from 'vue'
import {Loader2, ChevronsUpDown, Check, Copy, Trash2, Plus, Key} from 'lucide-vue-next'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {Label} from '@/components/ui/label'
import {Input} from '@/components/ui/input'
import {Button} from '@/components/ui/button'
import {Switch} from '@/components/ui/switch'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from '@/components/ui/command'
import i18next from 'i18next'
import {useColorMode, type ColorMode} from '@/composables/useColorMode'
import {useToast} from '@/composables/useToast'
import {useAppStore} from '@/stores/app'
import {useLocalStore} from '@/stores/local'
import {timezoneGroups, allTimezones} from '@/data/timezones'
import {cn} from '@/lib/utils'

const open = defineModel<boolean>('open', {required: true})

const appStore = useAppStore()
const localStore = useLocalStore()
const {mode: colorMode} = useColorMode()
const toast = useToast()

const loading = ref(false)
const saving = ref(false)
const tzOpen = ref(false)

const form = ref({
  name: '',
  lang: 'ru',
  gender: '' as string,
  timezone: 'UTC',
  digests: true,
})

const selectedTheme = ref<ColorMode>('auto')

const tzLabel = ref('')

function updateTzLabel(value: string) {
  const tz = allTimezones.find((z) => z.value === value)
  tzLabel.value = tz?.label ?? value
}

// API Keys state
const apiKeys = ref<{key_id: number; name: string; created_at: string; last_used_at: string | null}[]>([])
const newKeyName = ref('')
const creatingKey = ref(false)
const newlyCreatedKey = ref('')

watch(open, async (isOpen) => {
  if (!isOpen || !appStore.apiService) return

  loading.value = true
  newlyCreatedKey.value = ''
  try {
    const [user, keys] = await Promise.all([appStore.apiService.getUser(), appStore.apiService.getApiKeys()])
    form.value.name = user.name || ''
    form.value.lang = user.settings?.lang || 'ru'
    form.value.gender = user.settings?.gender || ''
    form.value.timezone = user.settings?.timezone || 'UTC'
    form.value.digests = user.settings?.digests ?? true
    selectedTheme.value = colorMode.value
    updateTzLabel(form.value.timezone)
    apiKeys.value = keys
  } catch (e) {
    toast.error('Не удалось загрузить настройки')
  } finally {
    loading.value = false
  }
})

watch(selectedTheme, (val) => {
  colorMode.value = val
})

function selectTimezone(value: string) {
  form.value.timezone = value
  updateTzLabel(value)
  tzOpen.value = false
}

async function save() {
  if (!appStore.apiService) return

  saving.value = true
  try {
    await appStore.apiService.updateUser({
      name: form.value.name,
      settings: {
        lang: form.value.lang,
        gender: (form.value.gender || undefined) as 'male' | 'female' | undefined,
        timezone: form.value.timezone,
        digests: form.value.digests,
      },
    })
    if (form.value.lang !== i18next.language) {
      await i18next.changeLanguage(form.value.lang)
    }
    localStore.lang = form.value.lang
    toast.success('Настройки сохранены')
    open.value = false
  } catch (e) {
    toast.error('Не удалось сохранить настройки')
  } finally {
    saving.value = false
  }
}

async function createApiKey() {
  if (!appStore.apiService || !newKeyName.value.trim()) return

  creatingKey.value = true
  try {
    const result = await appStore.apiService.createApiKey(newKeyName.value.trim())
    newlyCreatedKey.value = result.key
    apiKeys.value.unshift({
      key_id: result.key_id,
      name: result.name,
      created_at: result.created_at,
      last_used_at: null,
    })
    newKeyName.value = ''
  } catch (e) {
    toast.error('Не удалось создать ключ')
  } finally {
    creatingKey.value = false
  }
}

async function deleteApiKey(keyId: number) {
  if (!appStore.apiService) return

  try {
    await appStore.apiService.deleteApiKey(keyId)
    apiKeys.value = apiKeys.value.filter((k) => k.key_id !== keyId)
    toast.success('Ключ удалён')
  } catch (e) {
    toast.error('Не удалось удалить ключ')
  }
}

async function copyKey() {
  try {
    await navigator.clipboard.writeText(newlyCreatedKey.value)
    toast.success('Скопировано')
  } catch {
    toast.error('Не удалось скопировать')
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Не использовался'
  return new Date(dateStr).toLocaleDateString('ru-RU', {day: 'numeric', month: 'short', year: 'numeric'})
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="sm:max-w-md max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Настройки</DialogTitle>
        <DialogDescription>Настройте приложение под себя</DialogDescription>
      </DialogHeader>

      <div v-if="loading" class="flex items-center justify-center py-8">
        <Loader2 class="size-6 animate-spin text-muted-foreground" />
      </div>

      <div v-else class="grid gap-4 py-2">
        <!-- Тема -->
        <div class="grid gap-2">
          <Label>Тема</Label>
          <Select v-model="selectedTheme">
            <SelectTrigger>
              <SelectValue placeholder="Выберите тему" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Авто</SelectItem>
              <SelectItem value="light">Светлая</SelectItem>
              <SelectItem value="dark">Тёмная</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Имя -->
        <div class="grid gap-2">
          <Label>Имя</Label>
          <Input v-model="form.name" placeholder="Ваше имя" />
        </div>

        <!-- Язык -->
        <div class="grid gap-2">
          <Label>Язык</Label>
          <Select v-model="form.lang">
            <SelectTrigger>
              <SelectValue placeholder="Выберите язык" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ru">Русский</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Пол -->
        <div class="grid gap-2">
          <Label>Пол</Label>
          <Select v-model="form.gender">
            <SelectTrigger>
              <SelectValue placeholder="Выберите пол" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Мужской</SelectItem>
              <SelectItem value="female">Женский</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Часовой пояс -->
        <div class="grid gap-2">
          <Label>Часовой пояс</Label>
          <Popover v-model:open="tzOpen">
            <PopoverTrigger as-child>
              <Button
                variant="outline"
                role="combobox"
                :aria-expanded="tzOpen"
                class="w-full justify-between font-normal">
                {{ tzLabel || 'Выберите часовой пояс' }}
                <ChevronsUpDown class="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-[var(--reka-popover-trigger-width)] p-0" align="start">
              <Command>
                <CommandInput placeholder="Поиск..." />
                <CommandEmpty>Не найдено</CommandEmpty>
                <CommandList>
                  <CommandGroup v-for="group in timezoneGroups" :key="group.label" :heading="group.label">
                    <CommandItem
                      v-for="tz in group.zones"
                      :key="tz.value"
                      :value="tz.label"
                      @select="selectTimezone(tz.value)">
                      <Check :class="cn('mr-2 size-4', form.timezone === tz.value ? 'opacity-100' : 'opacity-0')" />
                      {{ tz.label }}
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <!-- Дайджесты -->
        <div class="flex items-center justify-between">
          <Label for="digests">Дайджесты</Label>
          <Switch id="digests" v-model="form.digests" />
        </div>

        <!-- API-ключи (MCP) -->
        <div class="grid gap-3 border-t pt-4">
          <div class="flex items-center gap-2">
            <Key class="size-4 text-muted-foreground" />
            <Label class="text-sm font-medium">API-ключи (MCP)</Label>
          </div>

          <!-- Банер с новым ключом -->
          <div v-if="newlyCreatedKey" class="rounded-md border border-yellow-500/50 bg-yellow-500/10 p-3 text-sm">
            <p class="mb-2 font-medium text-yellow-600 dark:text-yellow-400">
              Сохраните ключ — он больше не будет показан
            </p>
            <div class="flex items-center gap-2">
              <code class="flex-1 break-all rounded bg-muted px-2 py-1 text-xs">{{ newlyCreatedKey }}</code>
              <Button variant="outline" size="icon" class="shrink-0" @click="copyKey">
                <Copy class="size-4" />
              </Button>
            </div>
            <Button variant="ghost" size="sm" class="mt-2 w-full" @click="newlyCreatedKey = ''">Закрыть</Button>
          </div>

          <!-- Создание нового ключа -->
          <div class="flex gap-2">
            <Input v-model="newKeyName" placeholder="Имя ключа" class="flex-1" @keyup.enter="createApiKey" />
            <Button size="icon" :disabled="creatingKey || !newKeyName.trim()" @click="createApiKey">
              <Loader2 v-if="creatingKey" class="size-4 animate-spin" />
              <Plus v-else class="size-4" />
            </Button>
          </div>

          <!-- Список ключей -->
          <div v-if="apiKeys.length > 0" class="grid gap-2">
            <div
              v-for="key in apiKeys"
              :key="key.key_id"
              class="flex items-center justify-between rounded-md border px-3 py-2">
              <div class="min-w-0">
                <p class="truncate text-sm font-medium">{{ key.name }}</p>
                <p class="text-xs text-muted-foreground">{{ formatDate(key.last_used_at) }}</p>
              </div>
              <Button variant="ghost" size="icon" class="shrink-0 text-destructive" @click="deleteApiKey(key.key_id)">
                <Trash2 class="size-4" />
              </Button>
            </div>
          </div>
          <p v-else class="text-xs text-muted-foreground">Нет активных ключей</p>
        </div>
      </div>

      <DialogFooter v-if="!loading">
        <Button variant="outline" @click="open = false">Отмена</Button>
        <Button :disabled="saving" @click="save">
          <Loader2 v-if="saving" class="mr-2 size-4 animate-spin" />
          Сохранить
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
